// Libs
import React, { useState, useContext } from "react";
import { StoresContext, Stores } from "Globals/Stores";
import { useObserver, KeyValuePair, Grid, Else, FieldType, EditableInput } from "@shoothill/core";
import { Link as RouterLink } from "react-router-dom";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { RadioSVG } from "Globals/Views/IconSVGs/RadioSVG";
import { RadioCheckedSVG } from "Globals/Views/IconSVGs/RadioCheckedSVG";
import { Button, Table, TableHead, TableBody, TableRow, TableContainer, TableCell, Box, Checkbox, Typography } from "@material-ui/core";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DateFnsUtils from "@date-io/date-fns";
import styled from "styled-components";
import { BinSVG } from "Globals/Views/IconSVGs/BinSVG";

// App
import { theme } from "Globals/Styles/AppTheme";
import IncomeDetailsViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/IncomeDetailsViewModel";
import FinancialCommitmentViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/FinancialCommitmentViewModel";
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import { CurrencyInput } from "Globals/Views/CurrencyInput";

// Styles
import { StyledButton } from "Globals/Styles/ButtonStyles";
import { Row } from "Globals/Styles/LayoutStyles";
import { CustomDatePicker } from "Globals/Views/CustomDatePicker";
import { CustomInput } from "Globals/Styles/AppStyling";

export const StyledAddButton = styled(StyledButton)`
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 0px;
    margin: 0 0 1px 0;
`;
interface IProps {
    incomeDetailsViewModel: IncomeDetailsViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
    isPrimary?: boolean;
}

export const FinancialCommitmentView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [upsertViewModel] = useState(() => new FinancialCommitmentViewModel());
    return useObserver(() => (
        <>
            <Box style={{ backgroundColor: "#F0F0F0", padding: "15px" }}>
                {(upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving) == false && (
                    <>
                        <Row style={{ alignItems: "center" }}>
                            <CustomInput
                                label="What are the payments for?"
                                placeHolder="Enter the reason for the payments"
                                viewModel={upsertViewModel}
                                fieldName="purpose"
                                shrink={true}
                                inputProps={{
                                    disabled: upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving,
                                }}
                            />
                            <CurrencyInput
                                viewModel={upsertViewModel}
                                label={"How much are the payments?"}
                                placeholder={"Enter payment amount"}
                                fieldName={"amount"}
                                disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                            />
                            <Box>
                                <CustomDatePicker
                                    viewModel={upsertViewModel}
                                    label="Date of final payment"
                                    placeholder={"Enter date of final payment"}
                                    fieldName="paymentEndDate"
                                    value={upsertViewModel.model.paymentEndDate}
                                    onChange={(date) => {
                                        upsertViewModel.setDate("paymentEndDate", date);
                                    }}
                                    resetCallback={props.incomeDetailsViewModel.restartTimer}
                                    disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                                    disablePast={true}
                                />
                            </Box>

                            {props.isPrimary && props.applicationFormViewModel.getSortedApplicantViewModels.length > 1 && (
                                <Box>
                                    <Row style={{ alignItems: "center" }}>
                                        <Checkbox
                                            checked={upsertViewModel.getValue("sameForApplicantTwo")}
                                            onChange={(e) => {
                                                upsertViewModel.handleCheckboxChange("sameForApplicantTwo", e.target.checked);
                                            }}
                                            icon={<CheckboxSVG />}
                                            checkedIcon={<CheckboxCheckedSVG />}
                                            disabled={props.applicationFormViewModel.model.isSubmitted}
                                        />
                                        <Typography variant="body1" style={{ fontWeight: "bold" }}>
                                            Same for applicant 2
                                        </Typography>
                                    </Row>
                                </Box>
                            )}

                            <Box style={{ display: "flex", alignItems: "start", marginTop: "22px" }}>
                                <StyledAddButton
                                    variant="contained"
                                    disableElevation
                                    onClick={() => upsertViewModel.stash(props.incomeDetailsViewModel)}
                                    disabled={upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving}
                                >
                                    +
                                </StyledAddButton>
                            </Box>
                        </Row>
                    </>
                )}
                {props.incomeDetailsViewModel.getSortedFinancialCommitmentViewModles.length > 0 && (
                    <TableContainer>
                        <Table style={{ width: "100%" }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>What are the payments for?</TableCell>
                                    <TableCell align="center">What are the payments for?</TableCell>
                                    <TableCell align="center">Date payments to end</TableCell>
                                    {props.isPrimary && <TableCell align="center">Same for applicant 2</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {props.incomeDetailsViewModel.getSortedFinancialCommitmentViewModles.map((commitment) => (
                                    <TableRow key={commitment.model.id}>
                                        <TableCell component="th" scope="commitment">
                                            {commitment.model.purpose}
                                        </TableCell>
                                        <TableCell align="center">£{commitment.model.amount}</TableCell>
                                        <TableCell align="center">{commitment.getFormattedPaymentEndDate}</TableCell>
                                        {props.isPrimary && <TableCell align="center">{commitment.getValue("sameForApplicantTwo") ? "Yes" : "No"}</TableCell>}
                                        <TableCell align="center">
                                            {(upsertViewModel.IsLoading || props.applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving) ==
                                                false && (
                                                <Button
                                                    onClick={() => {
                                                        commitment.delete(props.incomeDetailsViewModel);
                                                    }}
                                                >
                                                    <BinSVG />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </>
    ));
};
